import { ITelemetryItem } from "@microsoft/applicationinsights-web";

/**
 * Single entry point deciding whether an Application Insights telemetry item is
 * expected noise that should NOT be sent. Returns `true` to drop the item.
 *
 * Compose new noise filters by adding an internal predicate below and OR-ing it
 * in here — callers (the telemetry initializer) only ever call this function.
 */
export function shouldDropTelemetry(item: ITelemetryItem): boolean {
    return isExpectedImagePollingNotFound(item) || isExpiredSignalRNegotiateUnauthorized(item);
}

/** Status code of a remote-dependency telemetry item, or NaN for anything else. */
function dependencyResponseCode(item: ITelemetryItem): number {
    if (item.baseType !== 'RemoteDependencyData' || !item.baseData) {
        return NaN;
    }
    return Number(item.baseData['responseCode']);
}

/** `"<METHOD> <path>"` name + full URL of a remote dependency, lower-cased; '' for anything else. */
function dependencyTarget(item: ITelemetryItem): string {
    if (item.baseType !== 'RemoteDependencyData' || !item.baseData) {
        return '';
    }
    return `${item.baseData['name'] ?? ''} ${item.baseData['data'] ?? ''}`.toLowerCase();
}

/**
 * The image-polling service (`libs/shared/util-image-polling`) issues repeated
 * HEAD requests while a thumbnail is still being generated server-side. A 404
 * there is the expected "not ready yet" signal, not a real failure — but App
 * Insights' automatic AJAX tracking records each one as a failed dependency.
 *
 * HEAD is not used anywhere else in the app, so `HEAD` + `404` cannot hide a
 * genuine failure. Fails open: the method is only available via the dependency
 * name (`"<METHOD> <path>"`); if that format changes the 404s simply reappear.
 */
function isExpectedImagePollingNotFound(item: ITelemetryItem): boolean {
    return dependencyResponseCode(item) === 404 && dependencyTarget(item).startsWith('head ');
}

/**
 * SignalR negotiate 401s. When a lesson page is left open until the access token
 * expires, the streaming hub reconnects indefinitely; every attempt POSTs to
 * `/streaminghub/negotiate` and gets a 401, flooding the dependencies table.
 *
 * The actionable signal is preserved by a single source-level log in
 * `StreamingHubService` (it logs once per connection on the first unauthorized
 * reconnect), so dropping the repeated dependency rows loses no information.
 */
function isExpiredSignalRNegotiateUnauthorized(item: ITelemetryItem): boolean {
    return dependencyResponseCode(item) === 401 && dependencyTarget(item).includes('/streaminghub/negotiate');
}
